/* Reusable admin image uploader.
 * Renders a preview + drop / browse box. On upload, calls the admin
 * endpoint and exposes the resulting URL via `onChange(url)`.
 */
import { useRef, useState } from "react";
import { Image as ImageIcon, Upload, X } from "lucide-react";
import { api } from "@/lib/api";

export default function ImageUpload({ value, onChange, label = "Image", testid }) {
    const fileRef = useRef(null);
    const [uploading, setUploading] = useState(false);
    const [err, setErr] = useState("");

    const handleFile = async (file) => {
        if (!file) return;
        setErr("");
        setUploading(true);
        try {
            const fd = new FormData();
            fd.append("file", file);
            const { data } = await api.post("/admin/uploads/image", fd, {
                headers: { "Content-Type": "multipart/form-data" },
            });
            onChange(data.url);
        } catch (e) {
            setErr(
                e?.response?.data?.detail || "Échec de l'envoi de l'image"
            );
        } finally {
            setUploading(false);
        }
    };

    return (
        <div className="space-y-3" data-testid={testid}>
            <label className="overline text-xv-gold block">{label}</label>
            <div className="flex items-start gap-5">
                <div className="relative w-32 h-32 bg-xv-cream border border-xv-line shrink-0 overflow-hidden">
                    {value ? (
                        <img
                            src={value}
                            alt=""
                            className="w-full h-full object-cover"
                        />
                    ) : (
                        <div className="w-full h-full flex items-center justify-center text-xv-text-secondary/40">
                            <ImageIcon strokeWidth={1.1} size={28} />
                        </div>
                    )}
                </div>
                <div className="flex-1 space-y-3">
                    <input
                        type="text"
                        value={value || ""}
                        onChange={(e) => onChange(e.target.value)}
                        placeholder="URL externe (ou téléchargez un fichier)"
                        className="xv-input w-full"
                    />
                    <input
                        type="file"
                        ref={fileRef}
                        accept="image/jpeg,image/png,image/webp,image/gif"
                        onChange={(e) => {
                            handleFile(e.target.files?.[0]);
                            e.target.value = "";
                        }}
                        className="hidden"
                    />
                    <div className="flex flex-wrap items-center gap-3">
                        <button
                            type="button"
                            onClick={() => fileRef.current?.click()}
                            disabled={uploading}
                            className="inline-flex items-center gap-2 px-4 py-2 text-[11px] tracking-[0.24em] uppercase border border-xv-midnight/40 text-xv-midnight hover:bg-xv-midnight hover:text-xv-ivory transition-colors disabled:opacity-40"
                        >
                            <Upload strokeWidth={1.3} size={13} />
                            {uploading ? "Envoi…" : "Télécharger une image"}
                        </button>
                        {value && (
                            <button
                                type="button"
                                onClick={() => onChange("")}
                                className="inline-flex items-center gap-1 text-[11px] tracking-[0.18em] uppercase text-red-700/80 hover:text-red-800"
                            >
                                <X strokeWidth={1.3} size={12} />
                                Retirer
                            </button>
                        )}
                    </div>
                    <p className="text-[11px] text-xv-text-secondary/65 italic leading-snug">
                        JPG, PNG, WebP ou GIF — max 8 Mo. L'image sera servie depuis votre domaine.
                    </p>
                    {err && (
                        <div className="text-[11.5px] text-red-700 border-l-2 border-red-700 pl-3 py-1 italic">
                            {err}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}
